import { ReactNode, createContext, useContext } from 'react';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { useGlobalRealtime } from '@/hooks/useGlobalRealtime';

interface RealtimeContextValue {
  // Currently side-effect only, no exposed values
  // Can be extended later for realtime status indicators
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/**
 * RealtimeProvider - Centralized Realtime Subscription Management
 * 
 * This provider ensures a single, stable Supabase Realtime channel
 * per restaurant session. Placing it at the app level (above routes)
 * prevents duplicate subscriptions that could occur when the
 * DashboardLayout re-renders.
 * 
 * Benefits:
 * - Single subscription per restaurant (no duplicates)
 * - Survives route changes
 * - Proper cleanup on restaurant switch
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { currentRestaurantId } = useRestaurant();
  
  // The hook handles all subscription logic internally
  // It will create a new channel when restaurantId changes
  // and cleanup the old one automatically
  useGlobalRealtime(currentRestaurantId);
  
  return (
    <RealtimeContext.Provider value={{}}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};
